import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiUnlock, FiStar, FiMusic, FiTrendingUp,
  FiHandshake, FiFileText, FiXCircle, FiAlertCircle, FiCheckCircle,
  FiTarget, FiBarChart2, FiRefreshCw, FiPlusCircle, FiInbox, FiTrash2,
} = FiIcons;

export const TYPE_CONFIG = {
  unlock:                    { icon: FiUnlock,      color: 'text-blue-400',     bg: 'bg-blue-500/10 border-blue-500/20',       label: 'Unlock' },
  subscription:              { icon: FiStar,         color: 'text-amber-400',    bg: 'bg-amber-500/10 border-amber-500/20',     label: 'Membership' },
  new_content:               { icon: FiMusic,        color: 'text-purple-400',   bg: 'bg-purple-500/10 border-purple-500/20',   label: 'New Drop' },
  milestone:                 { icon: FiTrendingUp,   color: 'text-green-400',    bg: 'bg-green-500/10 border-green-500/20',     label: 'Milestone' },
  partnership_request:       { icon: FiHandshake,    color: 'text-brand-pink',   bg: 'bg-brand-pink/10 border-brand-pink/20',   label: 'Partnership' },
  partnership_accepted:      { icon: FiCheckCircle,  color: 'text-green-400',    bg: 'bg-green-500/10 border-green-500/20',     label: 'Accepted' },
  partnership_declined:      { icon: FiXCircle,      color: 'text-red-400',      bg: 'bg-red-500/10 border-red-500/20',         label: 'Declined' },
  partnership_ended:         { icon: FiAlertCircle,  color: 'text-orange-400',   bg: 'bg-orange-500/10 border-orange-500/20',   label: 'Ended' },
  partnership_terms_updated: { icon: FiFileText,     color: 'text-sky-400',      bg: 'bg-sky-500/10 border-sky-500/20',         label: 'Terms' },
  milestone_completed:       { icon: FiCheckCircle,  color: 'text-emerald-400',  bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Done ✓' },
  milestone_overdue:         { icon: FiAlertCircle,  color: 'text-red-400',      bg: 'bg-red-500/10 border-red-500/20',         label: 'Overdue' },
  milestone_progress:        { icon: FiBarChart2,    color: 'text-brand-coral',  bg: 'bg-brand-coral/10 border-brand-coral/20', label: 'Progress' },
  milestone_status_change:   { icon: FiRefreshCw,    color: 'text-brand-pink',   bg: 'bg-brand-pink/10 border-brand-pink/20',   label: 'Status' },
  partnership_milestone_added:{ icon: FiPlusCircle,  color: 'text-indigo-400',   bg: 'bg-indigo-500/10 border-indigo-500/20',   label: 'New Goal' },
  partnership_digest:        { icon: FiInbox,        color: 'text-zinc-400',     bg: 'bg-zinc-700/30 border-zinc-700/40',       label: 'Digest' },
};

const PRIORITY_BADGE = {
  urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
  high:   'bg-brand-pink/15 text-brand-pink border-brand-pink/25',
  normal: '',
  low:    '',
};

const timeAgo = (ts) => {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationItem = ({ notification: n, onMarkRead, onDelete }) => {
  const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.unlock;
  const priorityBadge = PRIORITY_BADGE[n.priority] || '';
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !n.is_read && onMarkRead(n.id)}
      className={`relative flex items-start gap-3 p-4 rounded-2xl cursor-pointer transition-all group ${
        n.is_read
          ? 'hover:bg-white/[0.02]'
          : n.priority === 'urgent'
            ? 'bg-red-500/5 border border-red-500/15 hover:bg-red-500/8'
            : n.priority === 'high'
              ? 'bg-brand-pink/5 border border-brand-pink/15 hover:bg-brand-pink/8'
              : 'bg-white/[0.04] border border-white/8 hover:bg-white/[0.06]'
      }`}
    >
      {/* Unread pulse dot */}
      {!n.is_read && (
        <span className={`absolute top-4 right-4 w-2 h-2 rounded-full ${
          n.priority === 'urgent' ? 'bg-red-400 animate-ping' :
          n.priority === 'high'   ? 'bg-brand-pink animate-pulse' : 'bg-brand-coral'
        }`} />
      )}

      {/* Icon */}
      <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
        <SafeIcon icon={cfg.icon} className={`text-sm ${cfg.color}`} />
      </div>

      <div className="flex-1 min-w-0 pr-5 space-y-1">
        <p className={`text-xs font-black leading-tight ${n.is_read ? 'text-zinc-500' : 'text-white'}`}>
          {n.title}
        </p>
        <p className={`text-[11px] font-bold leading-relaxed ${n.is_read ? 'text-zinc-700' : 'text-zinc-400'}`}>
          {n.message}
        </p>

        <div className="flex items-center gap-2 flex-wrap pt-0.5">
          <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>
            {cfg.label}
          </span>
          {priorityBadge && (
            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${priorityBadge}`}>
              {n.priority}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-widest text-zinc-700">
            {timeAgo(n.created_at)}
          </span>
          {n.metadata?.amount && (
            <span className="text-[9px] font-black uppercase tracking-widest text-green-500">
              ${n.metadata.amount}
            </span>
          )}
        </div>
      </div>

      {/* Delete on hover */}
      {hovered && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(n.id); }}
          className="absolute top-3 right-3 p-1 rounded-lg text-zinc-700 hover:text-red-400 hover:bg-red-500/10 transition-all"
        >
          <SafeIcon icon={FiTrash2} className="text-[10px]" />
        </button>
      )}
    </div>
  );
};

export default NotificationItem;